import React, { useEffect, useState } from "react";
import {
    Container,
    Card,
    CardContent,
    Grid,
    Typography,
    TextField,
    Autocomplete,
    Box,
    Stack,
    Paper,
} from "@mui/material";

import {
    useGetHourlyActivitiesQuery,
    useGetHourlyLocationsQuery,
    useGetHourlyCourtsQuery,
    useFindHourlySlotsMutation,
} from "../../../store/hourlySlots/hourlySlotsApis";

import Breadcrumbs from "../../../components/layout/Breadcrumbs";
import Input from "../../../components/Common/Input";
import Button from "../../../components/Common/Button";


const HourlySlotsContainer = () => {
   
    const [date, setDate] = useState("");
    const [activity, setActivity] = useState(null);
    const [location, setLocation] = useState(null);
    const [court, setCourt] = useState(null);
    const [searchResults, setSearchResults] = useState([]);

    const {
        data: activities = [],
        isLoading: activitiesLoading,
        isError: activitiesError,
    } = useGetHourlyActivitiesQuery();

    const {
        data: locations = [],
        isLoading: locationsLoading,
        isError: locationsError,
    } = useGetHourlyLocationsQuery(
        activity?._id,
        {
            skip: !activity?._id,
        }
    );

    const {
        data: courts = [],
        isLoading: courtsLoading,
        isError: courtsError,
    } = useGetHourlyCourtsQuery(
        {
            activityId: activity?._id,
            locationId: location?._id,
        },
        {
            skip: !activity?._id || !location?._id,
        }
    );

    const [
        findHourlySlots,
        {
            isLoading: findSlotsLoading,
            isError: findSlotsError,
        },
    ] = useFindHourlySlotsMutation();

    const handleFind = async () => {
        if (!date || !activity?._id || !location?._id || !court?.court) {
            return;
        }

        try {
            const payload = {
                booking_date: date,
                activity_id: activity._id,
                location_id: location._id,
                court: court.court,
            };

            const result = await findHourlySlots(payload).unwrap();

            setSearchResults(result || []);
        } catch (error) {
            console.error("Failed to find hourly slots:", error);
            setSearchResults([]);
        }
    };

    const getEndTime = (time) => {
        if (!time) return "-";

        const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

        if (!match) {
            return "-";
        }

        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3].toUpperCase();

        // Convert to 24-hour format
        if (period === "AM" && hours === 12) {
            hours = 0;
        }

        if (period === "PM" && hours !== 12) {
            hours += 12;
        }

        hours += 1;

        // Handle 12 PM -> 1 PM, 11 PM -> 12 AM
        if (hours >= 24) {
            hours -= 24;
        }

        const endPeriod = hours >= 12 ? "PM" : "AM";
        let displayHours = hours % 12;

        if (displayHours === 0) {
            displayHours = 12;
        }

        return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${endPeriod}`;
    };

    return (
        <Stack spacing={1}>
                <Paper sx={{ marginBottom: "24px", padding: 1.5 }}>
                    <Grid container justifyContent="space-between">
                        <Grid item xs={6} sx={{ alignSelf: "center" }}>
                            <Typography variant="h6">Hourly Slots</Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper sx={{ marginBottom: "24px", padding: 1.5 }}>
                    <Grid container spacing={3} alignItems="stretch">
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Input
                                fullWidth
                                label="Select Date"
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                sx={{
                                    "& .MuiInputBase-root": {
                                        height: 56,
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Autocomplete
                                options={activities}
                                value={activity}
                                loading={activitiesLoading}
                                onChange={(event, value) => {
                                    setActivity(value);
                                    setLocation(null);
                                    setCourt(null);
                                }}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option._id === value?._id
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Activity"
                                        error={activitiesError}
                                        helperText={
                                            activitiesError
                                                ? "Failed to load activities"
                                                : ""
                                        }
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: 56,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Autocomplete
                                options={locations}
                                value={location}
                                loading={locationsLoading}
                                disabled={!activity}
                                onChange={(event, value) => {
                                    setLocation(value);
                                    setCourt(null);
                                }}
                                getOptionLabel={(option) => option.title || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option._id === value?._id
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Location"
                                        error={locationsError}
                                        helperText={
                                            locationsError
                                                ? "Failed to load locations"
                                                : ""
                                        }
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: 56,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Autocomplete
                                options={courts}
                                value={court}
                                loading={courtsLoading}
                                disabled={!location}
                                onChange={(event, value) => {
                                    setCourt(value);
                                }}
                                getOptionLabel={(option) => option.court || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.court === value?.court
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Court"
                                        error={courtsError}
                                        helperText={
                                            courtsError
                                                ? "Failed to load courts"
                                                : ""
                                        }
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: 56,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2.4}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleFind}
                                disabled={
                                    !date ||
                                    !activity ||
                                    !location ||
                                    !court ||
                                    findSlotsLoading
                                }
                                sx={{
                                    height: 56,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                }}
                            >
                                {findSlotsLoading ? "Finding..." : "Find Slots"}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper
                    sx={{
                        marginBottom: "24px",
                        padding: 1.5,
                    }}
                >
                    <Grid container justifyContent="space-between">
                        {/* <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                            }}
                        >
                            Search Results
                        </Typography> */}

                        {findSlotsError && (
                            <Box
                                sx={{
                                    width: "100%",
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    backgroundColor: "#fdecea",
                                    border: "1px solid #f5c2c0",
                                }}
                            >
                                <Typography
                                    color="error"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Failed to fetch hourly slots.
                                </Typography>
                            </Box>
                        )}

                        {searchResults.length === 0 && !findSlotsLoading ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    p: 6,
                                    textAlign: "center",
                                    border: "1px dashed #c7c7c7",
                                    borderRadius: 2,
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 1,
                                    }}
                                >
                                    No Booked or Pending Slots Found
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Select Date, Activity, Location and Court,
                                    then click "Find Slots".
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ width: "100%" }}>
                                {searchResults.map((slot, index) => (
                                    <Box
                                        key={
                                            slot.booking_record_id ||
                                            slot.booking_id ||
                                            index
                                        }
                                        sx={{
                                            border: "1px solid #e2e5e9",
                                            borderRadius: 2,
                                            mb: 2,
                                            overflow: "hidden",
                                            backgroundColor: "#fff",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                                                borderColor: "#c8ccd1",
                                            },
                                        }}
                                    >
                                        {/* Slot Header */}
                                        <Box
                                            sx={{
                                                px: 3,
                                                py: 2,
                                                backgroundColor: "#f7f8fa",
                                                borderBottom: "1px solid #e2e5e9",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 2,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontWeight: 600,
                                                        textTransform: "uppercase",
                                                        letterSpacing: 0.5,
                                                    }}
                                                >
                                                    Slot Time
                                                </Typography>

                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        mt: 0.3,
                                                    }}
                                                >
                                                    {slot.booking_time
                                                        ? `${slot.booking_time} - ${getEndTime(
                                                            slot.booking_time
                                                        )}`
                                                        : "-"}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    px: 1.8,
                                                    py: 0.7,
                                                    borderRadius: 5,
                                                    backgroundColor:
                                                        slot.status?.toLowerCase() ===
                                                        "confirmed"
                                                            ? "#e8f5e9"
                                                            : "#fff3e0",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 700,
                                                        textTransform: "capitalize",
                                                    }}
                                                >
                                                    {slot.status || "-"}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Booking Information */}
                                        <Box sx={{ p: 3 }}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Booking ID
                                                    </Typography>

                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            mt: 0.5,
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {slot.booking_id || "-"}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Booked By
                                                    </Typography>

                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            mt: 0.5,
                                                        }}
                                                    >
                                                        {slot.booked_by?.name ||
                                                            slot.booked_by?.full_name ||
                                                            slot.booked_by?.member_name ||
                                                            "-"}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        Amount
                                                    </Typography>

                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 700,
                                                            mt: 0.3,
                                                        }}
                                                    >
                                                        ₹{slot.total_amount || 0}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {/* Players */}
                                            {slot.players?.length > 0 && (
                                                <Box
                                                    sx={{
                                                        mt: 3,
                                                        pt: 3,
                                                        borderTop: "1px solid #eeeeee",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            Players
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {slot.players.length}{" "}
                                                            {slot.players.length === 1
                                                                ? "Player"
                                                                : "Players"}
                                                        </Typography>
                                                    </Box>

                                                    <Grid container spacing={1.5}>
                                                        {slot.players.map(
                                                            (player, playerIndex) => (
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    sm={6}
                                                                    md={4}
                                                                    key={playerIndex}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            p: 1.5,
                                                                            border: "1px solid #e6e6e6",
                                                                            borderRadius: 1.5,
                                                                            backgroundColor:
                                                                                "#fafafa",
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: 700,
                                                                            }}
                                                                        >
                                                                            {player.name ||
                                                                                player.full_name ||
                                                                                player.member_name ||
                                                                                `Player ${
                                                                                    playerIndex +
                                                                                    1
                                                                                }`}
                                                                        </Typography>

                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            {player.member_id
                                                                                ? `Member ID: ${player.member_id}`
                                                                                : "Non-Member"}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            )
                                                        )}
                                                    </Grid>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Grid>
                </Paper>
        </Stack>
    );
};

export default HourlySlotsContainer;